import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { IDSAlert, IDSButton, IDSInput, IDSSelect } from "@inera/ids-react";
import SubmissionStatusBadge from "./SubmissionStatusBadge";
import { formatDateTimeNoSeconds } from "../../utils/dateTime";
import {
  getArchiveOrganisations,
  searchArchivedReviewerSubmissions,
} from "../../services/E2EAPI";

const ALL_ORGANISATIONS = "__ALL__";

/**
 * Search across finished (approved/rejected) cases. Unlike the tabs above this
 * queries the server, so cases that are no longer part of the active list are
 * still findable. Results are scoped to the reviewer's departments server-side.
 */
export default function ArchiveSearch() {
  const navigate = useNavigate();

  const [organisation, setOrganisation] = useState(ALL_ORGANISATIONS);
  const [userQuery, setUserQuery] = useState("");
  const [organisations, setOrganisations] = useState([]);
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [hasSearched, setHasSearched] = useState(false);

  const runSearch = useCallback(
    async (filters) => {
      try {
        setLoading(true);
        setError("");

        const rows = await searchArchivedReviewerSubmissions({
          organisation:
            filters.organisation === ALL_ORGANISATIONS ? "" : filters.organisation,
          user: filters.user,
        });

        setResults(Array.isArray(rows) ? rows : []);
        setHasSearched(true);
      } catch (err) {
        console.error("Archive search failed", err);
        setError(err.message || "Kunde inte söka bland avslutade ärenden.");
        setResults([]);
      } finally {
        setLoading(false);
      }
    },
    []
  );

  // Load the organisation list and show the full archive to begin with.
  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        const names = await getArchiveOrganisations();
        if (!cancelled) setOrganisations(Array.isArray(names) ? names : []);
      } catch (err) {
        console.error("Failed to load archive organisations", err);
      }
    })();

    runSearch({ organisation: ALL_ORGANISATIONS, user: "" });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSubmit = (e) => {
    e.preventDefault();
    runSearch({ organisation, user: userQuery });
  };

  const handleReset = () => {
    setOrganisation(ALL_ORGANISATIONS);
    setUserQuery("");
    runSearch({ organisation: ALL_ORGANISATIONS, user: "" });
  };

  const userLabel = (row) => row.userName || row.userEmail || row.userId || "—";

  return (
    <div className="ids-mt-4">
      <form onSubmit={handleSubmit}>
        <div
          className="ids-mb-4"
          style={{ display: "flex", gap: "1rem", flexWrap: "wrap", alignItems: "flex-end" }}
        >
          <div style={{ minWidth: "260px" }}>
            <IDSSelect
              label="Organisation"
              id="archiveOrganisation"
              name="archiveOrganisation"
              value={organisation}
              onChange={(e) => setOrganisation(e.target.value)}
            >
              <option value={ALL_ORGANISATIONS}>Alla organisationer</option>
              {organisations.map((name) => (
                <option key={name} value={name}>
                  {name}
                </option>
              ))}
            </IDSSelect>
          </div>

          <div style={{ minWidth: "260px" }}>
            <IDSInput
              label="Användare"
              type="text"
              value={userQuery}
              placeholder="Namn eller e-post"
              onChange={(e) => setUserQuery(e.target.value)}
            />
          </div>

          <div style={{ display: "flex", gap: "0.5rem" }}>
            <IDSButton size="m" type="submit" disabled={loading}>
              {loading ? "Söker…" : "Sök"}
            </IDSButton>
            <IDSButton secondary size="m" type="button" onClick={handleReset} disabled={loading}>
              Rensa
            </IDSButton>
          </div>
        </div>
      </form>

      {error && (
        <div className="ids-mb-4">
          <IDSAlert type="error"><p>{error}</p></IDSAlert>
        </div>
      )}

      {!loading && hasSearched && results.length === 0 && !error && (
        <p className="ids-mt-4">Inga avslutade ärenden matchar sökningen.</p>
      )}

      {results.length > 0 && (
        <>
          <p style={{ color: "var(--ids-palette-neutral-60)", marginBottom: "0.75rem" }}>
            {results.length} avslutade ärende{results.length === 1 ? "" : "n"}
          </p>

          <div style={{ width: "100%", overflowX: "auto" }}>
            <table className="ids-mb-4" style={{ width: "100%", minWidth: "1200px" }}>
              <thead>
                <tr>
                  <th style={{ textAlign: "left", padding: "0.5rem 1rem" }}>Formulärnamn</th>
                  <th style={{ textAlign: "center", padding: "0.5rem 1rem" }}>Inlämnad av</th>
                  <th style={{ textAlign: "center", padding: "0.5rem 1rem" }}>Organisation</th>
                  <th style={{ textAlign: "center", padding: "0.5rem 1rem" }}>Status</th>
                  <th style={{ textAlign: "center", padding: "0.5rem 1rem" }}>Avslutad</th>
                  <th style={{ textAlign: "center", padding: "0.5rem 1rem" }}>Åtgärd</th>
                </tr>
              </thead>
              <tbody>
                {results.map((row) => (
                  <tr key={row.submissionId} style={{ height: "60px" }}>
                    <td style={{ textAlign: "left", maxWidth: "400px", wordBreak: "break-word", padding: "0.5rem 1rem" }}>
                      {row.formTitle}
                    </td>
                    <td style={{ textAlign: "center", padding: "0.5rem 1rem" }}>
                      <span style={{ display: "inline-flex", flexDirection: "column", lineHeight: 1.2 }}>
                        <span>{userLabel(row)}</span>
                        {row.userEmail && row.userName && (
                          <span style={{ fontSize: "0.75rem", color: "var(--ids-palette-neutral-60)" }}>
                            {row.userEmail}
                          </span>
                        )}
                      </span>
                    </td>
                    <td style={{ textAlign: "center", padding: "0.5rem 1rem" }}>
                      {row.organizationName || "—"}
                    </td>
                    <td style={{ textAlign: "center", padding: "0.5rem 1rem" }}>
                      <SubmissionStatusBadge status={row.status} />
                    </td>
                    <td style={{ textAlign: "center", padding: "0.5rem 1rem" }}>
                      {row.reviewedAt
                        ? formatDateTimeNoSeconds(row.reviewedAt)
                        : row.updatedAt
                        ? formatDateTimeNoSeconds(row.updatedAt)
                        : "—"}
                    </td>
                    <td style={{ textAlign: "center", padding: "0.5rem 1rem" }}>
                      <IDSButton
                        size="s"
                        style={{ minWidth: "160px" }}
                        onClick={() =>
                          navigate(`/e2e/reviewer/submissions/view/${row.submissionId}`)
                        }
                      >
                        Visa
                      </IDSButton>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
